import moment from 'moment';
import { useState } from 'react';
import { THUMBNAIL_IMAGE_SIZING } from '../../components/EntityImage';
import { GuestPicker } from '../../components/GuestPicker';
import { CloseIcon } from '../../icons/CloseIcon';
import { DatePicker } from '../DatePicker';
import { RateCodeField } from '../JourneyPicker/RateCodeField';
import {
  CategoryList,
  CategoryOption,
  CategoryThumb,
  CategoryThumbImg,
  CloseButton,
  Footer,
  HeaderRow,
  NextButton,
  ResetButton,
  Section,
  SectionBody,
  SectionHeader,
  SectionLabel,
  SectionValue,
  Sections,
  Wrapper,
} from './MobileJourneyPicker.styles';
import type {
  MobileJourneyPickerProps,
  MobileJourneyPickerSection,
} from './MobileJourneyPicker.types';

function formatRangeLabel(from?: string | null, to?: string | null): string | null {
  if (!from) return null;
  const fromM = moment(from, 'YYYY-MM-DD');
  if (!to || from === to) return fromM.format('D MMM');
  const toM = moment(to, 'YYYY-MM-DD');
  if (fromM.month() === toM.month() && fromM.year() === toM.year()) {
    return `${fromM.format('D')} - ${toM.format('D MMM')}`;
  }
  return `${fromM.format('D MMM')} - ${toM.format('D MMM')}`;
}

export function MobileJourneyPicker({
  categories,
  category,
  onCategoryChange,
  fromDate,
  toDate,
  availability,
  showPastDates = false,
  onRangeSelect,
  onMonthChange,
  adults,
  childCount,
  onAdultsChange,
  onChildrenChange,
  minAdults,
  maxAdults,
  minChildren,
  maxChildren,
  onClose,
  onReset,
  onNext,
  defaultOpenSection,
  visibleMonthCount = 2,
  showWho = true,
  isSingleDate = false,
  showRate = false,
  rateCode,
  onRateCodeChange,
  labels: l,
}: MobileJourneyPickerProps) {
  const showWhat = !!categories && categories.length > 0;
  const [open, setOpen] = useState<MobileJourneyPickerSection | null>(
    defaultOpenSection ?? (showWhat ? 'what' : 'when')
  );

  const toggle = (section: MobileJourneyPickerSection) => {
    setOpen((current) => (current === section ? null : section));
  };

  const currentCategory = categories?.find((c) => c.id === category);
  const dateRangeLabel = formatRangeLabel(fromDate, toDate);
  const guestsTotal = adults + childCount;

  const hasDates = !!fromDate;
  const hasGuests = guestsTotal > 0;
  const hasAnyValue = !!currentCategory || hasDates || hasGuests;

  return (
    <Wrapper>
      <HeaderRow>
        {onClose && (
          <CloseButton type="button" onClick={onClose} aria-label={l?.closeAriaLabel ?? 'Close'}>
            <CloseIcon size={16} />
          </CloseButton>
        )}
      </HeaderRow>

      <Sections>
        {showWhat && (
          <Section>
            <SectionHeader
              type="button"
              onClick={() => toggle('what')}
              aria-expanded={open === 'what'}
            >
              <SectionLabel>{l?.what ?? 'What?'}</SectionLabel>
              <SectionValue $placeholder={!currentCategory}>
                {currentCategory?.label ?? l?.addCategory ?? 'Add category'}
              </SectionValue>
            </SectionHeader>
            {open === 'what' && (
              <SectionBody>
                <CategoryList>
                  {categories!.map((cat) => (
                    <CategoryOption
                      key={cat.id}
                      type="button"
                      $active={cat.id === category}
                      onClick={() => {
                        onCategoryChange?.(cat.id);
                        setOpen('when');
                      }}
                    >
                      <span>{cat.label}</span>
                      <CategoryThumb>
                        {cat.imageUrl && (
                          <CategoryThumbImg
                            src={cat.imageUrl}
                            alt={cat.imageAlt ?? ''}
                            sizing={THUMBNAIL_IMAGE_SIZING}
                          />
                        )}
                      </CategoryThumb>
                    </CategoryOption>
                  ))}
                </CategoryList>
              </SectionBody>
            )}
          </Section>
        )}

        <Section $grow={open === 'when'}>
          <SectionHeader
            type="button"
            onClick={() => toggle('when')}
            aria-expanded={open === 'when'}
          >
            <SectionLabel>{l?.when ?? 'When?'}</SectionLabel>
            <SectionValue $placeholder={!dateRangeLabel}>
              {dateRangeLabel ?? l?.addDates ?? 'Add dates'}
            </SectionValue>
          </SectionHeader>
          {open === 'when' && (
            <SectionBody $grow $noPadding>
              <DatePicker
                checkIn={fromDate ?? null}
                checkOut={toDate ?? null}
                singleDateMode={isSingleDate}
                numberOfMonths={visibleMonthCount === 1 ? 1 : 2}
                availability={availability}
                showPastDates={showPastDates}
                onRangeSelect={(checkIn, checkOut) => onRangeSelect?.(checkIn, checkOut)}
                onMonthChange={onMonthChange}
                stacked
                labels={l?.calendar}
              />
            </SectionBody>
          )}
        </Section>

        {showWho && (
          <Section>
            <SectionHeader
              type="button"
              onClick={() => toggle('who')}
              aria-expanded={open === 'who'}
            >
              <SectionLabel>{l?.who ?? 'Who?'}</SectionLabel>
              <SectionValue $placeholder={!hasGuests}>
                {hasGuests
                  ? `${guestsTotal} ${guestsTotal === 1 ? 'Guest' : 'Guests'}`
                  : l?.addGuests ?? 'Add guests'}
              </SectionValue>
            </SectionHeader>
            {open === 'who' && (
              <SectionBody>
                <GuestPicker
                  adults={adults}
                  childCount={childCount}
                  onAdultsChange={onAdultsChange}
                  onChildrenChange={onChildrenChange}
                  minAdults={minAdults}
                  maxAdults={maxAdults}
                  minChildren={minChildren}
                  maxChildren={maxChildren}
                  renderInline
                  labels={l?.guestPicker}
                />
              </SectionBody>
            )}
          </Section>
        )}
        {showRate && onRateCodeChange && (
          <Section>
            <SectionBody $noPadding>
              <RateCodeField
                value={rateCode}
                placeholder={l?.rateCode ?? 'Rate code'}
                applyLabel={l?.rateApply ?? 'Apply'}
                clearLabel={l?.rateClear ?? 'Clear rate code'}
                onApply={(code) => onRateCodeChange(code)}
                onClear={() => onRateCodeChange('')}
              />
            </SectionBody>
          </Section>
        )}
      </Sections>

      <Footer>
        <ResetButton type="button" onClick={onReset} disabled={!onReset || !hasAnyValue}>
          {l?.reset ?? 'Reset'}
        </ResetButton>
        <NextButton type="button" onClick={onNext} disabled={!onNext}>
          {l?.next ?? 'Next'}
        </NextButton>
      </Footer>
    </Wrapper>
  );
}
